/**
 * Tech-page, university-page and /universities tracking.
 *
 * Every event is an ordinary trackClick (PostHog `utech_click` + the site's
 * own site_events mirror, which records `path` for every row), named with a
 * page prefix — `techpage_`, `unipage_`, `unis_` — so the admin "Tech URL
 * pages" tab can read a page's whole activity by action prefix. Props stay
 * small (ids and short strings): the store clips a props blob at 4 KB and a
 * clipped blob loses the whole beacon.
 *
 * TECHPAGE_EVENTS is the full catalogue (action → label + the props it
 * carries). The admin panel imports it to label rows, so an action sent
 * without an entry here shows up unlabelled there.
 */

import { useEffect, useRef } from 'react';
import { trackClick } from '../../../utils/analytics';

export interface TrackedEvent {
  label: string;
  props: string[];
}

export const TECHPAGE_EVENTS: Record<string, TrackedEvent> = {
  // ---- technology page ----
  techpage_view: { label: 'Tech page viewed', props: ['tech_id', 'state'] },
  techpage_section_view: { label: 'Section seen', props: ['section', 'mode', 'tech_id', 'state'] },
  techpage_scroll_depth: { label: 'Scrolled', props: ['depth', 'tech_id'] },
  techpage_engaged: { label: 'Stayed on the page', props: ['seconds', 'tech_id'] },
  techpage_cta_click: { label: 'CTA clicked', props: ['cta', 'position', 'tech_id', 'state'] },
  techpage_locked_click: { label: 'Locked section clicked', props: ['section', 'reason', 'tech_id', 'state'] },
  techpage_share: { label: 'Shared', props: ['channel', 'tech_id'] },
  techpage_bookmark: { label: 'Bookmark toggled', props: ['on', 'tech_id'] },
  techpage_login_prompt: { label: 'Asked to sign in', props: ['reason', 'tech_id'] },
  techpage_contact_open: { label: 'Contact panel opened', props: ['tech_id', 'state'] },
  techpage_contact_copy: { label: 'Contact detail copied', props: ['kind', 'tech_id'] },
  techpage_upgrade_click: { label: 'Upgrade clicked', props: ['from', 'tech_id'] },
  techpage_university_click: { label: 'University link clicked', props: ['code', 'position', 'tech_id'] },
  techpage_industry_click: { label: 'Industry link clicked', props: ['industry', 'tech_id'] },
  techpage_similar_click: { label: 'Similar technology clicked', props: ['target_id', 'rank', 'tech_id'] },
  techpage_breadcrumb_click: { label: 'Breadcrumb clicked', props: ['level', 'tech_id'] },
  techpage_trl_expand: { label: 'TRL explainer opened', props: ['tech_id'] },
  techpage_login_open: { label: 'Login opened', props: ['source'] },
  techpage_signup_open: { label: 'Sign up opened', props: ['source'] },
  techpage_login_success: { label: 'Logged in (page reloaded)', props: ['source'] },
  techpage_signup_success: { label: 'Signed up', props: ['source'] },
  // Fired as the visitor is sent back to the page after auth (a new account
  // after onboarding finished / was skipped). via: password | otp |
  // otp_signup | google | linkedin | login | next_param. The path is the page.
  techpage_return_after_auth: { label: 'Back on the page after sign-in', props: ['source', 'new_account', 'via', 'path'] },
  techpage_admin_state_switch: { label: 'Admin preview state switched', props: ['to', 'tech_id'] },
  techpage_outbound: { label: 'Outbound link clicked', props: ['host', 'tech_id'] },
  techpage_redirect: { label: 'Old URL redirected', props: ['from', 'to'] },
  techpage_not_found: { label: 'Page not found', props: ['path'] },
  techpage_rate_limited: { label: 'Rate limited', props: [] },
  // ---- university page ----
  unipage_view: { label: 'University page viewed', props: ['code', 'state'] },
  unipage_section_view: { label: 'Section seen', props: ['section', 'code'] },
  unipage_scroll_depth: { label: 'Scrolled', props: ['depth', 'code'] },
  unipage_search: { label: 'Searched technologies', props: ['q_len', 'code'] },
  unipage_filter: { label: 'Filter changed', props: ['filter', 'value', 'code'] },
  unipage_tech_click: { label: 'Technology clicked', props: ['target_id', 'rank', 'page', 'code'] },
  unipage_page: { label: 'Page changed', props: ['page', 'code'] },
  unipage_cta_click: { label: 'CTA clicked', props: ['cta', 'code'] },
  unipage_breadcrumb_click: { label: 'Breadcrumb clicked', props: ['level', 'code'] },
  unipage_outbound: { label: 'Outbound link clicked', props: ['host', 'code'] },
  // ---- /universities directory ----
  unis_university_click: { label: 'University card clicked', props: ['code', 'position'] },
  unis_request_banner_click: { label: '"Get listed" clicked', props: ['position'] },
  unis_request_open: { label: 'Listing form opened', props: [] },
  unis_request_submit: { label: 'Listing form sent', props: [] },
  unis_request_success: { label: 'Listing request received', props: ['ref'] },
  unis_request_error: { label: 'Listing form error', props: ['stage', 'field', 'status'] },
  unis_request_close: { label: 'Listing form closed', props: ['via', 'dirty'] },
};

/** Analytics must never break a click. */
export const tpTrack = (action: string, props?: Record<string, unknown>): void => {
  try { trackClick(action, props); } catch { /* ignore */ }
};

export const tpClip = (v: unknown, n = 200): string | null =>
  v === null || v === undefined || v === '' ? null : String(v).slice(0, n);

/**
 * One event per `[data-<attr>]` element, once per `key` (a page), when the
 * element reaches the upper two-thirds of the viewport. `payload` gets the
 * element's section name and returns the event props.
 */
export function useSectionViews(
  attr: 'techpageSection' | 'unipageSection',
  action: string,
  key: string | null,
  payload: (section: string, el: HTMLElement) => Record<string, unknown>,
): void {
  // Latest payload builder (the page's state can change under an admin preview).
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  useEffect(() => {
    if (!key || typeof IntersectionObserver === 'undefined') return;
    const seen = new Set<string>();
    const sel = attr === 'techpageSection' ? '[data-techpage-section]' : '[data-unipage-section]';
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        const el = e.target as HTMLElement;
        const name = el.dataset[attr] || '';
        if (!e.isIntersecting || !name || seen.has(name)) return;
        seen.add(name);
        tpTrack(action, payloadRef.current(name, el));
        io.unobserve(el);
      });
    }, { threshold: 0, rootMargin: '0px 0px -33% 0px' });
    // After paint, so sections rendered in the same commit are found.
    const t = window.setTimeout(() => {
      document.querySelectorAll<HTMLElement>(sel).forEach(el => io.observe(el));
    }, 0);
    return () => { window.clearTimeout(t); io.disconnect(); };
  }, [attr, action, key]);
}

/** <action> {depth, ...extra} at 25 / 50 / 75 / 100 %, once each per `key`. */
export function useScrollDepth(action: string, key: string | null, extra: Record<string, unknown>): void {
  const extraJson = JSON.stringify(extra);
  useEffect(() => {
    if (!key || typeof window === 'undefined') return;
    const hit = new Set<number>();
    let raf = 0;
    const check = () => {
      raf = 0;
      const doc = document.documentElement;
      const total = Math.max(doc.scrollHeight, document.body?.scrollHeight || 0);
      if (!total) return;
      const bottom = window.scrollY + window.innerHeight;
      [25, 50, 75, 100].forEach(d => {
        if (hit.has(d)) return;
        const reached = d === 100 ? bottom >= total - 4 : (bottom / total) * 100 >= d;
        if (!reached) return;
        hit.add(d);
        tpTrack(action, { depth: d, ...JSON.parse(extraJson) });
      });
    };
    // Only downward scrolls count: the resolver scrolls back to the top when
    // a new page comes in (smoothly, where the site sets scroll-behavior),
    // and that programmatic scroll is not the visitor reading.
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const down = y > lastY;
      lastY = y;
      if (down && !raf) raf = requestAnimationFrame(check);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [action, key, extraJson]);
}

/**
 * techpage_engaged {seconds: 15|60|180, tech_id}: time the tab was visible
 * on this page, not wall-clock time, so a page left open in a background tab
 * does not count as read.
 */
export function useEngagement(techId: string | null): void {
  useEffect(() => {
    if (!techId) return;
    const marks = [15, 60, 180];
    let visible = 0;
    const timer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      visible += 1;
      if (marks.includes(visible)) tpTrack('techpage_engaged', { seconds: visible, tech_id: techId });
      if (visible >= marks[marks.length - 1]) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [techId]);
}
